/**
 * components/icons/UiIcon.tsx —— **界面图标**统一集（U-16 图标族；U-S3 缺件建件）
 * 真源（102，逐字读）：`components/icons/UiIcon.vue`（85 行）——承大王 20260920「图标族：选 B」。
 * 依据：设计件 §9.5.5「全库 emoji 图标退役；引入 stroke 图标集（outline 风格、currentColor、
 *       1.5 stroke-width）；**禁引入运行时图标库依赖**（静态内联 SVG）」。
 *
 * 🔴 与 `XgIcon.tsx` 的分工（**单一职责**，两件不互代）：
 *   · `XgIcon` ＝ **业务/文件类型图标**（file/image/sheet/pdf/doc/ppt/audio/video/archive/paperclip/mic），
 *     由装配层按文件类型选名（产物列表行、工具卡结果）；
 *   · `UiIcon` ＝ **界面图标**（新建/设置/折叠/关闭/分栏…），由界面各组件按控件语义选名。
 *
 * 形制（与 102 **零视觉变化**）：`box` ＝ 该图标在**原组件里的 viewBox 边长**，路径**逐字照抄**旧树内联 SVG
 *   ⇒ 尺寸/圆角/线帽与原样一致；「统一到 16 网格」另批做（避免本批引入观感漂移）。
 * 用法：`<UiIcon name="settings" />` ／ `<UiIcon name="close" size={12} />`
 *
 * 纪律：零取数（props 注入）；不新增依赖（静态内联 SVG，不引任何图标库）；未登记名 ⇒ 回落 `plus`
 *   （不崩、不空白）；**缺的图标名不臆造**（键集＝102 图标表键，逐字）。
 */
import React from "react";

/** 图标名闭集（逐字＝102 图标表键；新增名须回流设计件，禁在此臆造） */
export const UI_ICON_NAMES = [
  // ── 左栏 ──
  "plus",
  "chevron-left",
  "chevron-right",
  "settings",
  // ── 右栏 tab 条 ──
  "close",
  "tab-new",
  "split",
  "presentation",
  "collapse",
  // ── 会话头 ──
  "menu",
  "panel-right",
  // ── 会话行 ──
  "message",
] as const;
export type UiIconName = (typeof UI_ICON_NAMES)[number];

export interface UiIconDef {
  /** 该图标在**原组件里的 viewBox 边长**（同时是缺省宽高） */
  box: number;
  /** 路径数组（逐字照抄 102 内联 SVG 的 `d`） */
  d: readonly string[];
}

/** 图标表：name → { box, d: [path…] }（全部自绘；16/14/12 网格见 box；禁第三方库） */
export const UI_ICONS: Readonly<Record<UiIconName, UiIconDef>> = Object.freeze({
  // ── 左栏 ──
  plus: { box: 16, d: ["M8 3v10M3 8h10"] }, // 新建会话
  "chevron-left": { box: 16, d: ["M10 3l-5 5 5 5"] }, // 折叠（收起左栏）
  "chevron-right": { box: 16, d: ["M6 3l5 5-5 5"] }, // 展开（折叠轨）
  settings: {
    // 设置（齿轮）
    box: 16,
    d: [
      "M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5",
      "M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41",
    ],
  },
  // ── 右栏 tab 条 ──
  close: { box: 12, d: ["M3 3l6 6M9 3l-6 6"] }, // 关闭标签
  "tab-new": { box: 14, d: ["M7 2v10M2 7h10"] }, // 新建标签
  split: {
    // 分栏（r1 圆角矩形 ＋ 中线）
    box: 14,
    d: [
      "M2 1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z",
      "M7 1v12",
    ],
  },
  presentation: {
    // 切换形态（左右两栏）
    box: 14,
    d: [
      "M2 1h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z",
      "M9 1h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z",
    ],
  },
  collapse: { box: 14, d: ["M10 3l-4 4 4 4"] }, // 折叠停靠面
  // ── 会话头 ──
  menu: { box: 16, d: ["M2 4h12M2 8h12M2 12h12"] }, // 打开会话导航（移动端）
  "panel-right": { box: 16, d: ["M3 3h10v10H3z", "M10 3v10"] }, // 展开停靠面
  // ── 会话行（20260921 左栏层次批：行首图形锚点；DSH/社区会话列表一律有行首图标）──
  message: {
    // 会话（气泡）
    box: 16,
    d: [
      "M13.5 8.5A4.5 3.5 0 0 1 9 12H6.5L4 14v-2a4.5 3.5 0 0 1-1.5-6.5A4.5 3.5 0 0 1 7 5h1.5A4.5 3.5 0 0 1 13.5 8.5Z",
    ],
  },
});

/** 未登记名回落（102：`ICONS[props.name] || ICONS.plus`——不崩、不空白） */
export const UI_ICON_FALLBACK: UiIconName = "plus";

/** 查表：名 → 图标定义；未登记名 ⇒ 回落 `plus`（不臆造、不抛错） */
export function uiIconDef(name: string): UiIconDef {
  if (typeof name === "string" && Object.prototype.hasOwnProperty.call(UI_ICONS, name)) {
    return (UI_ICONS as Readonly<Record<string, UiIconDef>>)[name];
  }
  return UI_ICONS[UI_ICON_FALLBACK];
}

export interface UiIconProps {
  name: string;
  /** 缺省（null）⇒ 用图标自身方框（保持原样）；给定 ⇒ 覆盖宽高（102 `size` 语义逐字） */
  size?: number | null;
}

const CSS = `
.xg-ui-icon {
  display: inline-block;
  vertical-align: -0.15em;
  flex: none;
}
`;
import { cssIds } from "../../styles/cssIds";
if (typeof document !== "undefined" && !document.getElementById(cssIds.uiIcon)) {
  const el = document.createElement("style");
  el.id = cssIds.uiIcon;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** 界面图标（静态内联 SVG；currentColor ＋ 1.5 stroke-width ＋ 圆头圆角） */
export function UiIcon({ name, size = null }: UiIconProps): React.ReactElement {
  const def = uiIconDef(name);
  const px = size ?? def.box;
  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${def.box} ${def.box}`}
      fill="none"
      aria-hidden="true"
      className="xg-ui-icon"
      data-ui-icon={name}
    >
      {def.d.map((p, i) => (
        <path
          key={i}
          d={p}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
